import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import type { ComboBoxOption } from '@gokrew/ui';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useAuth } from '@/context/AuthContext';
import { useHireJobOrgUnits } from '@/hooks/employees/useHireJobOrgUnits';
import type { AddEmployeeFormValues, HireJobPrefill } from '@/types/employee';
import {
  NEW_DESIGNATION_VALUE,
  resolveDepartmentPrefill,
  resolveDesignationPrefill,
} from './hireJobPrefill';

export interface JobPrefillOptions {
  designation?: ComboBoxOption;
  department?: ComboBoxOption;
}

interface JobPrefillSeedProps {
  job: HireJobPrefill;
  canCreateDesignation: boolean;
  /** The pickers wait for a branch: so does the prefill. */
  requiresBranch: boolean;
  /** The options picked, so the pickers can show them off their first page. */
  onSeeded: (options: JobPrefillOptions) => void;
}

/**
 * Fills Job Title and Department on a hire's Add employee form from the
 * recruiting job, once, and only fields nobody has picked yet. Renders
 * nothing; see `resolveDesignationPrefill` for what gets picked.
 */
export const JobPrefillSeed = ({
  job,
  canCreateDesignation,
  requiresBranch,
  onSeeded,
}: JobPrefillSeedProps) => {
  const { t } = useTranslation(TranslationNS.Employee);
  const { branchId: authBranchId, selectedCompany } = useAuth();
  const { values, setFieldValue } = useFormikContext<AddEmployeeFormValues>();
  const { designations, departments } = useHireJobOrgUnits(job);
  const seeded = useRef(false);
  const branchId = values.branchId || authBranchId;

  useEffect(() => {
    if (seeded.current || !designations || !departments) return;
    if (requiresBranch && !values.branchId) return;
    seeded.current = true;

    const options: JobPrefillOptions = {};
    const designation = resolveDesignationPrefill(
      designations,
      job,
      branchId,
      canCreateDesignation,
      selectedCompany?.language
    );
    if (designation && !values.designationId) {
      options.designation =
        designation.kind === 'existing'
          ? { value: designation.id, label: designation.name }
          : {
              value: NEW_DESIGNATION_VALUE,
              label: t('form.fields.designation_id.new_option', {
                name: designation.name,
              }),
            };
      void setFieldValue('designationId', options.designation.value, false);
    }
    const department = resolveDepartmentPrefill(
      departments,
      job.department,
      branchId
    );
    if (department && !values.departmentId) {
      options.department = { value: department.id, label: department.name };
      void setFieldValue('departmentId', department.id, false);
    }
    onSeeded(options);
  }, [
    designations,
    departments,
    requiresBranch,
    values.branchId,
    values.designationId,
    values.departmentId,
    branchId,
    job,
    canCreateDesignation,
    selectedCompany?.language,
    setFieldValue,
    onSeeded,
    t,
  ]);

  return null;
};
